import { MouseEvent, useState } from 'react'
import styled from 'styled-components'

import { DateRange, FILTER_DIVIDER, Filter } from '@percent/lemonade'

import {
  DateRangeValue,
  FilterDropdownAlign,
  ResolvedFilter,
} from '../../types/insights.types'
import { formatDateRange } from '../../utils/formatValue'

// Mantine's Popover.Target clones its single child to attach a ref; the Lemonade
// Filter isn't a forwardRef, so anchor it via a host div.
const Anchor = styled.div`
  display: inline-block;
`

// Sentinels for the non-preset options; prefixed so they can't collide with a
// backend preset key.
const ALL_TIME = '__all_time'
const CUSTOM = '__custom'
const POPOVER_Z_INDEX = 1300

export type InsightsDateRangeLabels = {
  /** Trigger label. */ label?: string
  allTime?: string
  custom?: string
  apply?: string
  clear?: string
}

export type InsightsDateRangeProps = {
  def: ResolvedFilter
  /** Currently-selected preset token, if any. */
  period?: string
  /** Custom range bounds, if a custom range is selected. */
  from?: string
  to?: string
  /** Emits the new selection as the mutually-exclusive `dateRange` value. */
  onChange: (value: DateRangeValue) => void
  labels?: InsightsDateRangeLabels
  /** Which edge the preset dropdown lines up with. Default: `'end'`. */
  dropdownAlign?: FilterDropdownAlign
  /**
   * Offer the "All time" option (default `true`). A report whose window is always
   * bounded - e.g. one where "no date filter" is not a meaningful state - passes
   * `false` to drop it; the control then starts on whichever preset is selected.
   */
  showAllTime?: boolean
  /** Keep the trigger's own label while "All time" is selected, instead of showing "All time". */
  labelWhenAllTime?: boolean
  testId?: string
}

/**
 * The date-range filter control: a preset dropdown (presets resolved per partner
 * by the backend, including fiscal year/quarter) + a custom range picker + an
 * "All time" clear. Emits a `period` token, a custom `{ from, to }`, or `undefined`
 * (all-time) — never computing dates itself.
 */
export function InsightsDateRange({
  def,
  period,
  from,
  to,
  onChange,
  labels,
  dropdownAlign = 'end',
  showAllTime = true,
  labelWhenAllTime = false,
  testId,
}: Readonly<InsightsDateRangeProps>) {
  const [pickerOpen, setPickerOpen] = useState(false)

  const isCustom = Boolean(from && to)
  const customLabel = isCustom
    ? formatDateRange(from as string, to as string)
    : labels?.custom ?? 'Custom range'
  const allTimeLabel = labels?.allTime ?? 'All time'
  const allTimeValue = labelWhenAllTime ? '' : ALL_TIME

  const options = [
    ...(showAllTime ? [{ label: allTimeLabel, value: allTimeValue }] : []),
    ...(def.presets ?? []).map((preset) => ({
      label: preset.label,
      value: preset.key,
    })),
    { label: '', value: FILTER_DIVIDER },
    { label: customLabel, value: CUSTOM },
  ]

  const selectedValue = isCustom ? CUSTOM : period ?? allTimeValue

  const handleChange = (value: string) => {
    if (value === FILTER_DIVIDER) return

    if (value === allTimeValue) {
      onChange(undefined)
      setPickerOpen(false)

      return
    }

    if (value === CUSTOM) {
      setPickerOpen(true)

      return
    }

    onChange({ period: value })
    setPickerOpen(false)
  }

  const setQueryParams = (changes: {
    startDate?: string
    endDate?: string
  }) => {
    if (changes.startDate && changes.endDate) {
      onChange({ from: changes.startDate, to: changes.endDate })
    } else if (
      changes.startDate === undefined &&
      changes.endDate === undefined
    ) {
      onChange(undefined)
    }
  }

  // While the picker is open, a click on the trigger dismisses it without also
  // toggling the Filter dropdown open underneath.
  const handleAnchorClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!pickerOpen) return
    event.stopPropagation()
    setPickerOpen(false)
  }

  return (
    <DateRange
      open={pickerOpen}
      setOpen={setPickerOpen}
      queryParams={{ startDate: from, endDate: to }}
      setQueryParams={setQueryParams}
      applyButtonText={labels?.apply ?? 'Apply'}
      clearButtonText={labels?.clear ?? 'Clear'}
      position="bottom-start"
      zIndex={POPOVER_Z_INDEX}
    >
      <Anchor onClickCapture={handleAnchorClickCapture}>
        <Filter
          label={labels?.label ?? def.label}
          options={options}
          selectedValue={selectedValue}
          onChange={handleChange}
          testId={testId}
          position={`bottom-${dropdownAlign}`}
          disableSelectedOption={false}
          compact
        />
      </Anchor>
    </DateRange>
  )
}
